import { UNSETTLED_CATEGORIES, type FinalCategory } from './final-category';

import type { FinalReport, FinalReportRow } from './final-report';

/**
 * The final report's numbers: records summed over the launched clients'
 * runs, warnings, and where every client ended up. Pure; the copy that reads
 * them is `report-overview.ts`.
 */

export interface RecordTotals {
    readonly discovered: number;
    readonly migrated: number;
    readonly failed: number;
    /** Skipped by a run that ended. */
    readonly skipped: number;
    /** Never attempted: the run was blocked before it touched a record. */
    readonly blocked: number;
    /** Still Pending or InProgress in a run nobody has seen finish. */
    readonly inProgress: number;
}

const ZERO: RecordTotals = {
    discovered: 0,
    migrated: 0,
    failed: 0,
    skipped: 0,
    blocked: 0,
    inProgress: 0,
};

/**
 * The run's totals when a refresh read them; otherwise counted from the
 * records' tracking. The runner's `skipped` is the REMAINDER — every record
 * neither migrated nor failed — so what it means depends on where the client
 * is: still in flight while the run is unsettled, never attempted when it was
 * blocked, skipped only once the run has ended.
 */
function rowTotals(row: FinalReportRow): RecordTotals {
    const totals = row.run?.totals ?? null;
    if (totals === null) {
        const tracked = row.records.filter((record) => record.tracking !== null);
        return {
            ...ZERO,
            discovered: tracked.length,
            migrated: tracked.filter((record) => record.tracking?.status === 'Migrated').length,
            failed: tracked.filter((record) => (record.tracking?.failure ?? null) !== null).length,
        };
    }
    const remainder = totals.skipped ?? 0;
    const bucket: keyof RecordTotals = UNSETTLED_CATEGORIES.has(row.category)
        ? 'inProgress'
        : row.category === 'blocked'
          ? 'blocked'
          : 'skipped';
    return {
        ...ZERO,
        discovered: totals.discovered ?? 0,
        migrated: totals.migrated ?? 0,
        failed: totals.failed ?? 0,
        [bucket]: remainder,
    };
}

/** Over the launched clients only: the others never had a run. */
export function recordTotals(report: Pick<FinalReport, 'rows'>): RecordTotals {
    return report.rows
        .filter((row) => row.launched)
        .map(rowTotals)
        .reduce(
            (sum, totals) => ({
                discovered: sum.discovered + totals.discovered,
                migrated: sum.migrated + totals.migrated,
                failed: sum.failed + totals.failed,
                skipped: sum.skipped + totals.skipped,
                blocked: sum.blocked + totals.blocked,
                inProgress: sum.inProgress + totals.inProgress,
            }),
            ZERO,
        );
}

export interface WarningTally {
    readonly total: number;
    readonly blocking: number;
    readonly codes: readonly string[];
}

export function warningTally(report: Pick<FinalReport, 'rows'>): WarningTally {
    const warnings = report.rows.flatMap((row) =>
        row.records.flatMap((record) => record.tracking?.warnings ?? []),
    );
    return {
        total: warnings.length,
        blocking: warnings.filter((warning) => warning.blocking === true).length,
        codes: [...new Set(warnings.map((warning) => warning.code))],
    };
}

/** The categories the "Not migrated" section lists (launched or not). */
export const NOT_MIGRATED_CATEGORIES: ReadonlySet<FinalCategory> = new Set([
    'not_selected',
    'not_migratable',
    'skipped',
    'error',
]);

/**
 * Where the launched clients are. Every launched client is in exactly one
 * bucket, so `completed + failed + blocked + errored + skipped + running +
 * unreadable === selected` — the lede can always be added up.
 */
export interface ClientCounts {
    readonly selected: number;
    readonly completed: number;
    readonly failed: number;
    readonly blocked: number;
    /** Launched, then an error: the start failed, or the run cannot be followed. */
    readonly errored: number;
    /** Launched, then skipped (e.g. the module was turned off for it). */
    readonly skipped: number;
    readonly running: number;
    /** The run exists but the last refresh could not read it. */
    readonly unreadable: number;
    readonly notMigrated: number;
}

export function clientCounts(report: Pick<FinalReport, 'rows'>): ClientCounts {
    const launched = report.rows.filter((row) => row.launched);
    const count = (...categories: FinalCategory[]) =>
        launched.filter((row) => categories.includes(row.category)).length;
    const completed = count('migrated', 'migrated_with_warnings');
    const failed = count('failed');
    const blocked = count('blocked');
    const skipped = count('skipped');
    const running = count('running', 'pending_refresh');
    const unreadable = count('refresh_failed');
    return {
        selected: launched.length,
        completed,
        failed,
        blocked,
        skipped,
        running,
        unreadable,
        // Everything else a launched client can be is an error of some kind.
        errored: launched.length - completed - failed - blocked - skipped - running - unreadable,
        notMigrated: report.rows.filter((row) => NOT_MIGRATED_CATEGORIES.has(row.category)).length,
    };
}
